import { InfoOutlined } from '@mui/icons-material';
import { Box, Modal, Paper, Popover, Typography } from '@mui/material';
import React, { useState, useRef } from 'react';

interface NewPageInfoPopoverProps {
  text: string;
  hover?: boolean;
  absoluteCenter?: boolean;
  width?: number;
  fontSize?: number;
}

export const NewPageInfoPopover = ({
  text,
  hover = false,
  width,
  absoluteCenter = false,
  fontSize = 24,
}: NewPageInfoPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const clearTimeout = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!hover) {
      setOpen(!open);
      if (!absoluteCenter) {
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    if (hover) {
      clearTimeout();
      setOpen(true);
      if (!absoluteCenter) {
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleMouseLeave = () => {
    if (hover) {
      // Add a delay to allow mouse to move to popover
      timeoutRef.current = window.setTimeout(() => {
        setOpen(false);
        setAnchorEl(null);
      }, 150);
    }
  };

  const handlePopoverMouseEnter = () => {
    if (hover) {
      clearTimeout();
      setOpen(true);
    }
  };

  const handlePopoverMouseLeave = () => {
    if (hover) {
      setOpen(false);
      setAnchorEl(null);
    }
  };

  const handleClose = () => {
    clearTimeout();
    setOpen(false);
    setAnchorEl(null);
  };

  const containerRef = React.useRef<HTMLDivElement>(null);

  // For hover mode, wrap everything to handle mouse events properly
  if (hover) {
    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'inline-flex',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <InfoOutlined
            sx={{
              color: '#7B8794',
              fontSize: `${fontSize}px`,
            }}
          />
        </Box>

        {!absoluteCenter ? (
          <Popover
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            container={containerRef.current}
            sx={{ zIndex: 1300, pointerEvents: 'none' }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                onMouseEnter: handlePopoverMouseEnter,
                onMouseLeave: handlePopoverMouseLeave,
                sx: {
                  borderRadius: '8px',
                  boxShadow: '0px 8px 16px rgba(67, 66, 66, 0.15)',
                  px: '20px',
                  py: '15px',
                  maxWidth: width ? `${width}px !important` : 'auto',
                  pointerEvents: 'auto',
                },
              },
            }}
          >
            <Typography
              fontSize="14px"
              fontWeight="400"
              textAlign="center"
              textTransform="none"
            >
              {text}
            </Typography>
          </Popover>
        ) : (
          <Modal open={open} onClose={handleClose} sx={{ zIndex: 1400 }}>
            <Paper
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '8px',
                boxShadow: '0px 8px 16px rgba(67, 66, 66, 0.15)',
                px: '30px',
                py: '30px',
                width: 247,
              }}
            >
              <Typography fontSize="14px" fontWeight="400" textTransform="none">
                {text}
              </Typography>
            </Paper>
          </Modal>
        )}
      </Box>
    );
  }

  // Click mode
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'inline-flex',
      }}
    >
      <Box
        sx={{
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={handleClick}
      >
        <InfoOutlined
          sx={{
            color: '#7B8794',
            fontSize: `${fontSize}px`,
          }}
        />
      </Box>

      {!absoluteCenter ? (
        <Popover
          open={open}
          onClose={handleClose}
          anchorEl={anchorEl}
          container={containerRef.current}
          sx={{ zIndex: 1300 }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                boxShadow: '0px 8px 16px rgba(67, 66, 66, 0.15)',
                px: '20px',
                py: '15px',
                maxWidth: width ? `${width}px !important` : 'auto',
              },
            },
          }}
        >
          <Typography
            fontSize="14px"
            fontWeight="400"
            textAlign="center"
            textTransform="none"
          >
            {text}
          </Typography>
        </Popover>
      ) : (
        <Modal open={open} onClose={handleClose} sx={{ zIndex: 1400 }}>
          <Paper
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '8px',
              boxShadow: '0px 8px 16px rgba(67, 66, 66, 0.15)',
              px: '30px',
              py: '30px',
              width: 247,
            }}
          >
            <Typography fontSize="14px" fontWeight="400" textTransform="none">
              {text}
            </Typography>
          </Paper>
        </Modal>
      )}
    </Box>
  );
};
